import * as React from "react";

import { AlertTitle, Typography } from "@mui/material";
import * as Sentry from "@sentry/nextjs";
import Link from "next/link";

import { AlertType } from "context/alertcontext";
import { MyOrgContext } from "context/myorgcontext";
import { UserContext } from "context/usercontext";
import { Org, useGetMyOrg } from "src/domain/administrative/config-management/api/getOrg";
import useStore from "src/store/store";

import useAlert from "../hooks/Alert";

const useValidateOrgConfig = (org: Org) => {
  const alerter = useAlert();
  const user = React.useContext(UserContext);

  return React.useCallback(() => {
    if (!org || !user) {
      return;
    }

    alerter(AlertType.None, null);

    if (user.is_org_admin && org.blockWorkflowCreation()) {
      alerter(
        AlertType.Warning,
        <>
          <AlertTitle>Incomplete AWS Settings</AlertTitle>
          <Typography variant="body2">
            Your organization&apos;s AWS settings are incomplete. Please review actions item(s) below to enable workflow
            creation for your organization.
          </Typography>
          <ul>
            {org.actionNeeded().invalidCredentials && (
              <li>
                <Typography variant="body2">
                  Please fill out <Link href="/organization/awscredentials">AWS access settings</Link>
                </Typography>
              </li>
            )}
            {org.actionNeeded().invalidNetwork && (
              <li>
                <Typography variant="body2">
                  Please fill out <Link href="/organization/awsnetwork">AWS network settings</Link>
                </Typography>
              </li>
            )}
          </ul>
        </>
      );
    }
  }, [alerter, org, user]);
};

const MyOrg = ({ children }) => {
  // This is necessary to rerender this component on cred change, which is necessary
  // to invoke the api endpoint when credentials become available
  useStore((state) => state.creds);

  const { data: org, error } = useGetMyOrg();

  const validate = useValidateOrgConfig(org);

  React.useEffect(() => {
    validate();
  }, [validate]);

  if (error) {
    Sentry.captureException(error);
    return <div className="centered-disp">Unable to determine organization. Contact support.</div>;
  }

  if (org) {
    Sentry.setTag("org", org.name());
  }

  return <MyOrgContext.Provider value={org}>{children}</MyOrgContext.Provider>;
};

export default MyOrg;
